import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { LiveKitRoom, VideoConference } from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';
import { Box, Spinner, Text, Center, Button, useToast, VStack, useBreakpointValue } from '@chakra-ui/react';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';
import io from 'socket.io-client';

import CustomMeetingLayout from './CustomMeetingLayout';

export default function MeetingRoom() {
  const { roomName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState("");
  const [error, setError] = useState(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [socket, setSocket] = useState(null);
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const livekitUrl = process.env.REACT_APP_LIVEKIT_URL || "ws://localhost:7880";
  // Fallback to localhost if no env var, assuming standard local dev port 5000
  const backendUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Get secretKey from navigation state (passed from MeetingsPage)
  const secretKey = location.state?.secretKey || "";

  useEffect(() => {
    if (!user) return;

    const fetchToken = async (keyToUse = secretKey) => {
      try {
        const response = await axiosInstance.post(`/meetings/token`, {
          meetingId: roomName,
          secretKey: keyToUse
        });
        if (response.data.success) {
          setToken(response.data.token);
        } else {
          setError(response.data.error || "Failed to get token");
        }
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.error || "خطأ في الانضمام إلى الحصة. تأكد من الرقم السري أو أن الحصة لا تزال نشطة.");
      }
    };

    const newSocket = io(backendUrl);
    setSocket(newSocket);

    if (user.role === 'student') {
      setIsWaiting(true);
      newSocket.emit("request_admission", { roomName, user });

      newSocket.on("admission_result", ({ approved, secretKey: hostProvidedKey }) => {
        if (approved) {
          setIsWaiting(false);
          fetchToken(hostProvidedKey);
        } else {
          setError("تم رفض طلب انضمامك من قبل المعلم.");
          setIsWaiting(false);
        }
      });
    } else {
      // Instructors join the socket room to receive admission requests
      newSocket.emit("join_meeting_room", { roomName, user });
      fetchToken();
    }

    return () => {
      newSocket.disconnect();
    };
  }, [roomName, secretKey, user, backendUrl]);

  if (error) {
    return (
      <Center h="100vh" flexDirection="column" bg="gray.900" color="white">
        <Text fontSize="xl" color="red.400" mb={4}>{error}</Text>
        <Button colorScheme="blue" onClick={() => navigate(-1)}>العودة</Button>
      </Center>
    );
  }

  if (isWaiting) {
    return (
      <Center h="100vh" flexDirection="column" bg="gray.900" color="white">
        <Spinner size="xl" color="orange.400" mb={4} />
        <Text fontSize="xl">أنت في غرفة الانتظار...</Text>
        <Text fontSize="md" color="gray.400">يرجى الانتظار حتى يسمح لك المعلم بالدخول</Text>
      </Center>
    );
  }

  if (!token) {
    return (
      <Center h="100vh" bg="gray.900" color="white">
        <Spinner size="xl" color="teal.400" />
        <Text ml={4}>جاري الانضمام...</Text>
      </Center>
    );
  }

  return (
    <Box position="fixed" top={0} left={0} bottom={0} right={isMobile ? 0 : "70px"} overflow="hidden" bg="black" zIndex={10}>
      <style>{`
        .my-custom-livekit-room {
          --lk-bg: #0B0E14;
          --lk-control-bar-bg: #111418;
          --lk-accent: #FFB800; 
          --lk-accent-fg: #000000;
          --lk-danger: #E53E3E;
          --lk-font-family: 'Tajawal', sans-serif;
          --lk-border-radius: 12px;
        }

        /* Hide English Text in Control Bar */
        .lk-control-bar .lk-button {
          font-size: 0 !important;
        }
        .lk-control-bar .lk-button * {
          font-size: 0 !important;
        }
        .lk-control-bar .lk-button svg {
          font-size: 1rem !important; /* Reset font size for SVGs if needed */
        }

        /* Control Bar Premium Look */
        .lk-control-bar {
          border-top: 2px solid #FFB800 !important;
          background: rgba(17, 20, 24, 0.95) !important;
          backdrop-filter: blur(10px);
          padding: 12px !important;
        }

        /* Icon-Only Control Bar Buttons */
        .lk-control-bar .lk-button {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          background-color: transparent !important;
          border: none !important;
          border-radius: 12px !important;
          padding: 12px !important;
          min-width: 48px;
          min-height: 48px;
          box-shadow: none !important;
          color: white !important;
          transition: background-color 0.2s ease !important;
        }
        
        .lk-control-bar .lk-button svg {
          width: 24px !important;
          height: 24px !important;
          fill: white !important;
          color: white !important;
        }

        .lk-control-bar .lk-button:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
          transform: none !important;
          box-shadow: none !important;
        }

        /* Leave Button (Icon Only, Red) */
        .lk-control-bar .lk-disconnect-button {
          background-color: rgba(229, 62, 62, 0.15) !important;
          color: #E53E3E !important;
        }
        .lk-control-bar .lk-disconnect-button svg {
          fill: #E53E3E !important;
          color: #E53E3E !important;
        }
        .lk-control-bar .lk-disconnect-button:hover {
          background-color: #E53E3E !important;
          color: white !important;
        }
        .lk-control-bar .lk-disconnect-button:hover svg {
          fill: white !important;
          color: white !important;
        }

        /* Microphone & Camera States */
        /* Active (ON) - Green Icon */
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="true"] svg, 
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="true"] svg,
        .lk-control-bar .lk-button[data-lk-source="camera"].lk-button-active svg, 
        .lk-control-bar .lk-button[data-lk-source="microphone"].lk-button-active svg {
          fill: #48BB78 !important;
          color: #48BB78 !important;
        }
        
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="true"] svg path, 
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="true"] svg path,
        .lk-control-bar .lk-button[data-lk-source="camera"].lk-button-active svg path, 
        .lk-control-bar .lk-button[data-lk-source="microphone"].lk-button-active svg path {
          fill: #48BB78 !important;
        }

        /* Inactive (OFF) - Red Icon */
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="false"] svg, 
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="false"] svg,
        .lk-control-bar .lk-button[data-lk-source="camera"]:not([aria-pressed="true"]):not(.lk-button-active) svg, 
        .lk-control-bar .lk-button[data-lk-source="microphone"]:not([aria-pressed="true"]):not(.lk-button-active) svg {
          fill: #E53E3E !important;
          color: #E53E3E !important;
        }

        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="false"] svg path, 
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="false"] svg path,
        .lk-control-bar .lk-button[data-lk-source="camera"]:not([aria-pressed="true"]):not(.lk-button-active) svg path, 
        .lk-control-bar .lk-button[data-lk-source="microphone"]:not([aria-pressed="true"]):not(.lk-button-active) svg path {
          fill: #E53E3E !important;
        }

        /* Screen Share Active State */
        .lk-control-bar .lk-button[data-lk-source="screen_share"][aria-pressed="true"] svg,
        .lk-control-bar .lk-button[data-lk-source="screen_share"].lk-button-active svg {
          fill: #FFB800 !important;
          color: #FFB800 !important;
        }

        /* Chat Panel Styling */
        .lk-chat {
          border-left: 1px solid rgba(255, 184, 0, 0.2) !important;
          background-color: #111418 !important;
        }
        
        .lk-chat-header {
          border-bottom: 1px solid rgba(255, 184, 0, 0.2) !important;
          color: #FFB800 !important;
          font-family: 'Tajawal', sans-serif;
        }
      `}</style>
      <LiveKitRoom
        video={true}
        audio={true}
        token={token}
        serverUrl={livekitUrl}
        onDisconnected={() => navigate(-1)}
        data-lk-theme="default"
        style={{ height: '100%', width: '100%' }}
      >
        <VideoConference />
        {socket && <CustomMeetingLayout socket={socket} roomName={roomName} secretKey={secretKey} user={user} />}
      </LiveKitRoom>
    </Box>
  );
}
